import { useState } from "react";
import Topbar from "../components/Topbar";
import ChartSection from "./analyticsDashboard/components/ChartSection";
import DynamicsChart from "./analyticsDashboard/components/DynamicsChart";
import DynamicsTableCard from "./analyticsDashboard/components/DynamicsTableCard";
import FiltersSection from "./analyticsDashboard/components/FiltersSection";
import KpiSection from "./analyticsDashboard/components/KpiSection";
import ObjectSelectCard from "./analyticsDashboard/components/ObjectSelectCard";
import PriceDetailsModalTest from "./analyticsDashboard/components/PriceDetailsModalTest";
import TableCard from "./analyticsDashboard/components/TableCard";
import { CHART_PALETTE, DEFAULT_FILTERS } from "./analyticsDashboard/constants";
import { TEST_AVERAGE_TABLE, TEST_AVG_DYNAMICS, TEST_CHART_DATA, TEST_CHECK_IN_RANGE, TEST_FILTER_OPTIONS, TEST_HOTELS, TEST_KPI, TEST_MIN_DYNAMICS, TEST_MIN_TABLE, TEST_PRICE_DETAILS_AVG, TEST_PRICE_DETAILS_MIN } from "./analyticsDashboard/testData";
import type { GridRow } from "./analyticsDashboard/types";
import { toRows } from "./analyticsDashboard/utils";

export default function AnalyticsDashboardTestPage() {
  const [filters, setFilters] = useState({
    ...DEFAULT_FILTERS,
    hotel: TEST_HOTELS[0]?.value ?? "",
    date: TEST_CHECK_IN_RANGE.from,
    dateIntervalFrom: TEST_CHECK_IN_RANGE.from,
    dateIntervalTo: TEST_CHECK_IN_RANGE.to,
  });
  const [daysStay, setDaysStay] = useState<1 | 3 | 5>(1);
  const [appliedAt, setAppliedAt] = useState<string | null>(null);
  const [visibleCount, setVisibleCount] = useState(10);
  const [activeTab, setActiveTab] = useState<"price" | "min" | "avg">("price");
  const [detailsOpen, setDetailsOpen] = useState(false);
  const [detailsData, setDetailsData] = useState(TEST_PRICE_DETAILS_AVG);
  const [detailsTitle, setDetailsTitle] = useState("Детальная таблица OTA");
  const [detailsSubtitle, setDetailsSubtitle] = useState<string | undefined>(undefined);

  const averageRows = toRows(TEST_AVERAGE_TABLE, 0);
  const minRows = toRows(TEST_MIN_TABLE, 100);

  const handleApply = () => {
    setAppliedAt(new Date().toLocaleTimeString("ru-RU", { hour: "2-digit", minute: "2-digit" }));
  };
  const handleReset = () => {
    setFilters((prev) => ({
      ...DEFAULT_FILTERS,
      hotel: prev.hotel,
      date: TEST_CHECK_IN_RANGE.from,
      dateIntervalFrom: TEST_CHECK_IN_RANGE.from,
      dateIntervalTo: TEST_CHECK_IN_RANGE.to,
    }));
    setAppliedAt(new Date().toLocaleTimeString("ru-RU", { hour: "2-digit", minute: "2-digit" }));
  };
  const handleOpenDetails = (rowDate: string, metric: "avg" | "min") => {
    setDetailsTitle("Детальная таблица OTA");
    setDetailsSubtitle(`Дата: ${rowDate}`);
    setDetailsData(metric === "min" ? TEST_PRICE_DETAILS_MIN : TEST_PRICE_DETAILS_AVG);
    setDetailsOpen(true);
  };

  return (
    <div className="min-h-screen bg-gradient-to-b from-slate-50 via-slate-50 to-slate-100 text-slate-900">
      <Topbar />

      <main className="px-6 py-8">
        <div className="mx-auto flex max-w-[1280px] flex-col gap-6">
          <section className="relative overflow-hidden rounded-3xl border border-white/70 bg-white/90 p-6 shadow-[0_18px_40px_rgba(15,23,42,0.08)] backdrop-blur">
            <div className="pointer-events-none absolute inset-y-0 right-0 w-40 translate-x-10 rounded-full bg-gradient-to-b from-indigo-100 to-purple-100 blur-3xl opacity-60" />
            <div className="flex flex-col gap-4 lg:flex-row lg:items-center lg:justify-between">
              <div>
                <h1 className="mt-2 text-3xl font-bold text-slate-900">Сравнение цен с конкурентами</h1>
                <p className="mt-2 text-sm text-slate-500">Тестовые данные для отладки UI.</p>
              </div>
              <div className="flex flex-col gap-3 sm:flex-row sm:items-center">
                <div className="flex items-center gap-3 self-start sm:self-auto">
                  <span className="text-xs font-semibold uppercase tracking-[0.08em] text-indigo-500">Срок заселения</span>
                  <div className="inline-flex rounded-full border border-indigo-200 bg-white p-1 shadow-sm">
                    {[
                      { value: 1 as const, label: "1 ночь" },
                      { value: 3 as const, label: "3 ночи" },
                      { value: 5 as const, label: "5 ночей" },
                    ].map((option) => {
                      const isActive = daysStay === option.value;
                      return (
                        <button
                          key={option.value}
                          type="button"
                          onClick={() => {
                            setDaysStay(option.value);
                            setAppliedAt(new Date().toLocaleTimeString("ru-RU", { hour: "2-digit", minute: "2-digit" }));
                          }}
                          className={[
                            "rounded-full px-3 py-1 text-xs font-semibold transition",
                            isActive ? "bg-indigo-600 text-white" : "text-slate-600 hover:bg-indigo-50",
                          ].join(" ")}
                          aria-pressed={isActive}
                        >
                          {option.label}
                        </button>
                      );
                    })}
                  </div>
                </div>
                <div className="rounded-2xl border border-indigo-100 bg-indigo-50/70 px-5 py-4 text-sm text-indigo-700">
                  {appliedAt
                    ? `Фильтры обновлены в ${appliedAt}`
                    : "Выберите объект, задайте фильтры и нажмите «Применить»"}
                </div>
              </div>
            </div>
          </section>

          <ObjectSelectCard
            value={filters.hotel}
            options={TEST_HOTELS}
            onChange={(value) => setFilters((prev) => ({ ...prev, hotel: value }))}
          />

          <section className="rounded-3xl border border-slate-200 bg-white/90 p-2 shadow-[0_10px_24px_rgba(15,23,42,0.06)]">
            <div className="grid grid-cols-1 gap-2 sm:grid-cols-3">
              {[
                { id: "price", label: "Цена относительно конкурентов" },
                { id: "min", label: "Тренды минимальной цены" },
                { id: "avg", label: "Тренды по средним ценам" },
              ].map((tab) => {
                const isActive = activeTab === tab.id;
                return (
                  <button
                    key={tab.id}
                    type="button"
                    onClick={() => setActiveTab(tab.id as "price" | "min" | "avg")}
                    className={[
                      "w-full rounded-2xl px-4 py-2 text-sm font-semibold transition",
                      isActive
                        ? "bg-indigo-600 text-white shadow-[0_10px_24px_rgba(79,70,229,0.35)]"
                        : "text-slate-500 hover:bg-slate-100",
                    ].join(" ")}
                  >
                    {tab.label}
                  </button>
                );
              })}
            </div>
          </section>

          <FiltersSection
            filters={filters}
            options={TEST_FILTER_OPTIONS}
            onChange={setFilters}
            onApply={handleApply}
            onReset={handleReset}
            checkInRange={TEST_CHECK_IN_RANGE}
            showCompetitors={activeTab === "price"}
            showDateInterval={activeTab !== "price"}
          />

          {activeTab === "price" && (
            <>
              <KpiSection items={TEST_KPI} />

              <section className="grid gap-6 lg:grid-cols-2">
                <TableCard
                  title="Средняя цена конкурентов"
                  rows={averageRows}
                  accent="violet"
                  visibleCount={visibleCount}
                  onPriceClick={(row: GridRow) => handleOpenDetails(row.date, "avg")}
                />
                <TableCard
                  title="Минимальная цена конкурентов"
                  rows={minRows}
                  accent="emerald"
                  visibleCount={visibleCount}
                  onPriceClick={(row: GridRow) => handleOpenDetails(row.date, "min")}
                />
              </section>

              <div className="mt-3 flex flex-wrap items-center justify-end gap-2 text-xs text-slate-500">
                <span>Отображать записей:</span>
                <select
                  className="cursor-pointer rounded-full border border-slate-200 bg-white px-3 py-1 text-xs font-semibold text-slate-700 outline-none"
                  value={visibleCount}
                  onChange={(event) => setVisibleCount(Number(event.target.value))}
                >
                  {[5, 10, 25, 50, 100, 150].map((option) => (
                    <option key={option} value={option}>
                      {option}
                    </option>
                  ))}
                </select>
              </div>

              <ChartSection data={TEST_CHART_DATA} palette={CHART_PALETTE} />
            </>
          )}

          {activeTab === "min" && (
            <>
              <DynamicsChart title="Тренды минимальной цены" data={TEST_MIN_DYNAMICS} yLabel="Минимальная цена" />
              <div className="mt-6">
                <DynamicsTableCard title="Тренды минимальной цены" data={TEST_MIN_DYNAMICS} fullCellHeatmap alignRight />
              </div>
            </>
          )}

          {activeTab === "avg" && (
            <>
              <DynamicsChart title="Тренды по средним ценам" data={TEST_AVG_DYNAMICS} yLabel="Средняя цена" />
              <div className="mt-6">
                <DynamicsTableCard title="Тренды по средним ценам" data={TEST_AVG_DYNAMICS} fullCellHeatmap alignRight />
              </div>
            </>
          )}
        </div>
      </main>

      <PriceDetailsModalTest
        open={detailsOpen}
        title={detailsTitle}
        subtitle={detailsSubtitle}
        data={detailsData}
        loading={false}
        onClose={() => setDetailsOpen(false)}
      />
    </div>
  );
}
